// app/javascript/controllers/ats_enrichment_controller.js
//
// Resume Enrichment panel: AI coaching on phrasing / verbs / metrics.
// Three-step flow: Analyze -> Generate Enhancements -> Apply Enhancements.
// Per-resume endpoints need the selected resume id (pushed by the coordinator
// via resumeIdValue). Real API (legacy lines 494-562):
//   POST /api/v1/ats_optimizer/enrichment/analyze/:id  -> { questions: [...] }
//   POST /api/v1/ats_optimizer/enrichment/enhance       { resume_id, answers } -> { enhancements: [...] }
//   POST /api/v1/ats_optimizer/enrichment/apply/:id     { enhancements } -> { resume_id }
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["questions", "actions", "preview", "status"]
  static values = { resumeId: String }

  connect() { this.enhancements = [] }

  async analyze() {
    if (!this.resumeIdValue) return this.#status("Select a resume first")
    this.#status("Analyzing resume...")
    try {
      const data = await this.#json(
        `/api/v1/ats_optimizer/enrichment/analyze/${encodeURIComponent(this.resumeIdValue)}`,
        "POST", {}
      )
      this.enhancements = []
      this.#renderQuestions(data.questions || [])
      this.#renderPreview([])
      this.#status("Answer the questions, then Generate")
    } catch (e) {
      this.#status(`Analyze failed: ${e.message}`)
    }
  }

  async generateEnhancements() {
    if (!this.resumeIdValue) return this.#status("Select a resume first")
    const answers = this.#collectAnswers()
    if (!answers.length) return this.#status("Answer at least one question")
    this.#status("Generating enhancements...")
    try {
      const data = await this.#json("/api/v1/ats_optimizer/enrichment/enhance", "POST", {
        resume_id: this.resumeIdValue,
        answers
      })
      this.enhancements = data.enhancements || []
      this.#renderPreview(this.enhancements)
      this.#status(this.enhancements.length ? "Enhancements ready — Apply to update resume" : "No enhancements suggested")
    } catch (e) {
      this.#status(`Generate failed: ${e.message}`)
    }
  }

  async applyEnhancements() {
    if (!this.resumeIdValue) return this.#status("Select a resume first")
    if (!this.enhancements.length) return this.#status("Generate enhancements first")
    this.#status("Applying enhancements...")
    try {
      const data = await this.#json(
        `/api/v1/ats_optimizer/enrichment/apply/${encodeURIComponent(this.resumeIdValue)}`,
        "POST", { enhancements: this.enhancements }
      )
      this.enhancements = []
      this.#renderPreview([])
      // Coordinator re-fetches + re-renders the resume preview with the update.
      this.dispatch("resume-result-changed", {
        detail: { resumeId: data.resume_id || this.resumeIdValue },
        prefix: "ats-optimizer"
      })
      this.#status("Enhancements applied")
    } catch (e) {
      this.#status(`Apply failed: ${e.message}`)
    }
  }

  #renderQuestions(questions) {
    this.questionsTarget.replaceChildren()
    this.questionsTarget.classList.toggle("hidden", questions.length === 0)
    if (this.hasActionsTarget) this.actionsTarget.classList.toggle("hidden", questions.length === 0)
    for (const q of questions) {
      const wrap = document.createElement("div")
      wrap.innerHTML = `
        <label class="block text-label-sm text-on-surface-variant mb-1">${this.#escape(q.label || q.question || "")}</label>
        <textarea data-question-id="${this.#escape(q.id)}"
                  rows="2"
                  class="w-full p-2 bg-surface-container-lowest border border-border-light rounded
                         text-body-sm focus:ring-1 focus:ring-primary focus:border-primary"></textarea>
      `
      this.questionsTarget.appendChild(wrap)
    }
  }

  #collectAnswers() {
    return [...this.questionsTarget.querySelectorAll("textarea")]
      .map(t => ({ id: t.dataset.questionId, answer: t.value }))
      .filter(a => a.answer.trim().length)
  }

  #renderPreview(enhancements) {
    if (!this.hasPreviewTarget) return
    this.previewTarget.replaceChildren()
    this.previewTarget.classList.toggle("hidden", enhancements.length === 0)
    for (const e of enhancements) {
      const div = document.createElement("div")
      div.className = "p-3 bg-surface-container-low rounded border border-border-light text-body-sm"
      div.textContent = e.text || e.bullet || e.suggestion || JSON.stringify(e)
      this.previewTarget.appendChild(div)
    }
  }

  async #json(url, method, body) {
    const r = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content
      },
      body: JSON.stringify(body || {})
    })
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    return r.json().catch(() => ({}))
  }

  #status(msg) { if (this.hasStatusTarget) this.statusTarget.textContent = msg }
  #escape(s) { return String(s).replace(/[&<>"']/g, c => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[c])) }
}
